import { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import { useSelector, useDispatch } from 'react-redux';
import CardCarousel from '@cont/PreSubscribed/CardCarousel';
import PlusCourseCard from '@comp/PlusCourseCard/PlusCourseCard';
import Image from '@base/Image';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import { enrollInTrialClasses } from '@actions/batches';
import useWindowSize from '@hooks/useWindowSize';
import { getIsSubscribedToGoal } from '@selectors/subscription';
import { processCourse } from '@api/processors/course';
import Course from '@models/Course';

const Wrapper = styled.div`
  grid-column: span 24;
  display: grid;
  grid-template-columns: repeat(24, ${Spacings.SPACING_8B});
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_12B};
  ${mediaQuery.uptoMobile} {
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    margin-top: ${Spacings.SPACING_8B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const TitleWrapper = styled.div`
  grid-column: 2/8;
  top: 235px;
  z-index: 2;
  ${mediaQuery.uptoMobile} {
    top: ${Spacings.SPACING_12B};
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B};
    grid-column: span 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: ${Spacings.SPACING_2B};
  }
`;

const Description = styled(P1)`
  color: var(--color-text-secondary);
`;

const NotifyWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_5B};
  border: 1px solid var(--color-divider);
  box-sizing: border-box;
  border-radius: ${Spacings.SPACING_1B};
  width: max-content;
  margin-top: ${Spacings.SPACING_4B};
  background: ${({ $clicked }) =>
    $clicked ? 'var(--color-divider)' : 'var(--color-base-1)'};
  cursor: ${({ $clicked }) => ($clicked ? 'not-allowed' : 'pointer')};
  ${({ $clicked }) =>
    $clicked && `padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_5B}`}
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const NotifyButton = styled.div`
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.BOLD};
  color: ${({ $clicked }) =>
    $clicked ? 'var(--color-text-secondary)' : 'var(--color-text-primary)'};
  margin-left: ${({ $clicked }) =>
    $clicked ? Spacings.SPACING_0B : Spacings.SPACING_3B};
`;

const InnerWrapper = styled.div`
  grid-column: 9/25;
  display: grid;
  grid-template-columns: repeat(16, ${Spacings.SPACING_8B});
  grid-column-gap: ${Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_4B};
  z-index: 1;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-column: span 10;
    grid-row-gap: 0;
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    & > a {
      grid-column: span 10;
    }
  }
  & > div:first-child {
    width: 750px;
    ${mediaQuery.uptoMobile} {
      width: 100vw;
    }
  }
  .card-carosel-container {
    ${mediaQuery.uptoMobile} {
      padding-bottom: ${Spacings.SPACING_0B};
      & > div:first-child {
        margin-left: ${Spacings.SPACING_0B};
      }
    }
  }
`;

const ClassCard = styled(PlusCourseCard)`
  width: 272px;
  margin-right: ${Spacings.SPACING_5B};
  margin-bottom: ${Spacings.SPACING_8B};
  .batch-thumbnail {
    width: 100%;
    height: 152px;
  }
  ${mediaQuery.uptoMobile} {
    .batch-thumbnail {
      width: 100%;
      height: 152px;
    }
  }
`;
const CONFIG = {
  title: 'Trial classes',
  description: 'Free classes conducted by the educators of this batch'
};
const formatByCourseModal = (trialClassesInfo = []) => {
  return trialClassesInfo.map((trialClass) => {
    const classData = Course.getEntityData(trialClass);
    if (trialClass?.author) {
      const { first_name, last_name } = trialClass.author;
      classData.authors[0].name = `${first_name ?? ''} ${last_name ?? ''}`;
    }
    classData.topicGroups = [trialClass.topic_group];
    if (classData.nextTrack.item) {
      classData.nextTrack = {
        rank: classData.nextTrack.item.content_type_item_rank,
        liveAtString: classData.nextTrack.item.properties.live_at
      };
    }
    return classData;
  });
};

const formatCourseData = (trialClassesInfo) => {
  return trialClassesInfo.map((trialClass) => {
    const classData = processCourse({
      ...trialClass,
      ...(trialClass.programme || {})
    });
    if (trialClass?.author) {
      const { firstName, lastName } = trialClass.author;
      classData.authors[0].name = `${firstName ?? ''} ${lastName ?? ''}`;
    }
    classData.topicGroups = [trialClass.topicGroup];
    if (classData.nextTrack) {
      classData.nextTrack = {
        rank:
          classData.nextTrack.contentTypeItemRank || classData.nextTrack.rank,
        liveAtString: classData.nextTrack.properties.liveAt
      };
    }
    return classData;
  });
};

const TrialClasses = ({
  trialClasses,
  batchUID,
  goalUID,
  config,
  disabledTopicGroup,
  hideLessonCount,
  isBatchGroup,
  step
}) => {
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const isSubscribed = useSelector(getIsSubscribedToGoal(goalUID));
  const [notifyClasses, setNotifyClasses] = useState(false);
  const { data: trialClassesInfo, metaInfo: { showNotifyMe = false } = {} } =
    trialClasses;
  if (isSubscribed || !trialClassesInfo?.length) return null;
  const { title = CONFIG.title, description = CONFIG.description } = config;
  const classes = isBatchGroup
    ? formatByCourseModal(trialClassesInfo)
    : formatCourseData(trialClassesInfo);

  const handleNotifyMe = () => {
    if (!notifyClasses) {
      dispatch(enrollInTrialClasses(batchUID)).then((res) => {
        if (res?.result) {
          setNotifyClasses(true);
        }
      });
    }
  };

  return (
    <Wrapper>
      <TitleWrapper>
        <H4>{title}</H4>
        {!isMobile ? <Description>{description}</Description> : null}
        {showNotifyMe && (
          <NotifyWrapper $clicked={notifyClasses} onClick={handleNotifyMe}>
            {!notifyClasses ? (
              <Image width={24} height={24} src="notification.svg" alt="-" />
            ) : null}
            <NotifyButton $clicked={notifyClasses}>
              {notifyClasses ? 'We’ll notify you' : 'Notify me'}
            </NotifyButton>
          </NotifyWrapper>
        )}
      </TitleWrapper>
      <InnerWrapper>
        <CardCarousel rightShadowDiv={false} step={step} type="dummy">
          {classes.map((trialClass) => (
            <ClassCard
              key={trialClass.uid}
              course={trialClass}
              disabledTopicGroup={disabledTopicGroup}
              hideLessonCount={hideLessonCount}
            />
          ))}
        </CardCarousel>
      </InnerWrapper>
    </Wrapper>
  );
};

export default TrialClasses;

TrialClasses.propTypes = {
  trialClasses: PropTypes.objectOf(PropTypes.any).isRequired,
  batchUID: PropTypes.string.isRequired,
  goalUID: PropTypes.string.isRequired,
  config: PropTypes.objectOf(PropTypes.any),
  disabledTopicGroup: PropTypes.bool,
  hideLessonCount: PropTypes.bool,
  isBatchGroup: PropTypes.bool,
  step: PropTypes.number
};

TrialClasses.defaultProps = {
  config: CONFIG,
  disabledTopicGroup: false,
  hideLessonCount: false,
  isBatchGroup: false,
  step: 1
};
